import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, useMemo } from 'react'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { PublicRoute } from '@/components/auth/PublicRoute'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import ROUTES from '@/utils/constants/routes'
import MainWrapper from '@/components/layout/MainWrapper'
import { getIsAdmin } from '@/utils/routeUtils'

// Lazy load components for better performance
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const DispatchLogin = lazy(() => import('@/pages/auth/DispatchLogin'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ChangePassword = lazy(() => import('@/pages/auth/ResetPassword'))
const SetPassword = lazy(() => import('@/pages/auth/SetPassword'))
const ResidencyInfo = lazy(() => import('@/pages/auth/ResidencyInfo'))
const AdminDashboard = lazy(() => import('@/pages/dashboard/AdminDashboard'))
const DispatcherDashboard = lazy(
  () => import('@/pages/dashboard/DispatcherDashboard')
)
const CasesPage = lazy(() => import('@/pages/CaseOverview'))
const CaseDetailsPage = lazy(() => import('@/pages/CaseDetails'))
const AmbulanceProviders = lazy(() => import('@/pages/AmbulanceProviders'))
const RespondersPage = lazy(() => import('@/pages/Responders'))
const HospitalsPage = lazy(() => import('@/pages/Hospitals'))
const LeaderboardPage = lazy(() => import('@/pages/Leaderboard'))
const AdminShiftManagement = lazy(
  () => import('@/pages/shift-management/ShiftManagement')
)
const DispatcherShiftManagement = lazy(
  () => import('@/pages/shift-management/ShiftManagementUserPage')
)
const MapPage = lazy(() => import('@/pages/Map'))
const UnauthorizedPage = lazy(() => import('@/pages/Unauthorized'))
const NotFoundPage = lazy(() => import('@/pages/NotFound'))

/**
 * Dynamic Dashboard component that renders the appropriate dashboard based on user role
 */
const Dashboard = () => {
  const isAdmin = useMemo(() => getIsAdmin(), [])
  return isAdmin ? <AdminDashboard /> : <DispatcherDashboard />
}

const ShiftManagement = () => {
  const isAdmin = useMemo(() => getIsAdmin(), [])
  return isAdmin ? <AdminShiftManagement /> : <DispatcherShiftManagement />
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner />
  </div>
)

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.home}
          element={
            <PublicRoute allowAuthenticated>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.login}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.dispatchLogin}
          element={
            <PublicRoute>
              <DispatchLogin />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.forgotPassword}
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.resetPassword}
          element={
            <PublicRoute>
              <ChangePassword />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.setPassword}
          element={
            <PublicRoute>
              <SetPassword />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.residencyInfo}
          element={
            <PublicRoute>
              <ResidencyInfo />
            </PublicRoute>
          }
        />

        <Route
          path={ROUTES.dashboard}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <Dashboard />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.cases}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <CasesPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={`${ROUTES.cases}/:caseId`}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <CaseDetailsPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.responders}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <RespondersPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.leaderboard}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <LeaderboardPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.map}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <MapPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.hospitals}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <HospitalsPage />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.dispatchers}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <div>Dispatchers Page</div>
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.ambulanceProviders}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <AmbulanceProviders />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.settings}
          element={
            <ProtectedRoute>
              <div className="p-8">
                <h1 className="text-2xl font-bold">Settings</h1>
                <p>Application settings</p>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.profile}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <div>Profile Page</div>
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.shiftManagement}
          element={
            <ProtectedRoute>
              <MainWrapper>
                <ShiftManagement />
              </MainWrapper>
            </ProtectedRoute>
          }
        />

        {/* Error Routes */}
        <Route path={ROUTES.unauthorized} element={<UnauthorizedPage />} />
        <Route path={ROUTES.notFound} element={<NotFoundPage />} />

        {/* Catch all route - redirect to 404 */}
        <Route path="*" element={<Navigate to={ROUTES.notFound} replace />} />
      </Routes>
    </Suspense>
  )
}
